/**
 * Admin Portal → Codes: the pure part of the one Codes area (Codes PR-8, 2026-09-30; the lead's
 * unified-codes design §2.1 and §2.2; docs/architecture/38-PARTNER-ATTRIBUTION.md §18 has every
 * response shape). Routes, the old routes' redirects, the vocabulary each screen speaks, the
 * catalog's query string, and the lines each screen shows — so jest pins them without a DOM.
 *
 * Web-only (the admin SPA).
 */

import { canWrite } from '../partners/partnersLogic';
import type { ProductSlots } from '../../../components/admin/storeAccessLogic';

// ═════════════════════════════════════════════════════════════════════════════
// routes
// ═════════════════════════════════════════════════════════════════════════════

export const CODES_ROUTES = {
  overview: '/admin/codes',
  all: '/admin/codes/all',
  batches: '/admin/codes/all/batches',
  offers: '/admin/codes/offers',
  store: '/admin/codes/store',
  storeSetup: '/admin/codes/store/setup',
  issue: '/admin/codes/issue',
  issueHouse: '/admin/codes/issue/house',
  issuePartner: '/admin/codes/issue/partner',
  api: '/admin/codes/api',
  activity: '/admin/codes/activity',
} as const;

export const batchPath = (batchId: string): string => `${CODES_ROUTES.batches}/${encodeURIComponent(batchId)}`;

/**
 * The routes this area replaced, and where each lands now — a bookmark keeps working, its query
 * string kept (`/admin/codes/batches?owner=house` → `/admin/codes/all/batches?owner=house`).
 * `/admin/growth` (Summer50 and one-off codes) lands on Issue codes, where "Generate pool codes"
 * and "+ One-off code" live; `/admin/store-access` on Store setup, the page it was.
 */
export const LEGACY_REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  { from: '/admin/growth', to: CODES_ROUTES.issue },
  { from: '/admin/store-access', to: CODES_ROUTES.storeSetup },
  { from: '/admin/codes/house', to: CODES_ROUTES.issueHouse },
  { from: '/admin/codes/batches', to: CODES_ROUTES.batches },
  { from: '/admin/codes/batches/:batchId', to: `${CODES_ROUTES.batches}/:batchId` },
];

/** A redirect's target: its `:params` filled in (encoded), the old address's query string kept. */
export function redirectTarget(to: string, params: Record<string, string | undefined>, search: string): string {
  const path = to.replace(/:([A-Za-z]+)/g, (_m, name: string) => encodeURIComponent(params[name] ?? ''));
  const q = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : '';
  return `${path}${q}`;
}

/** A sidebar favorite saved under an old path, moved to the NAV ITEM that now holds its page (review
 *  of #4329, F2: a favorite must point at a sidebar item or the sidebar drops it). Unknown paths are
 *  kept as they are. */
const LEGACY_FAVORITES: Record<string, string> = {
  '/admin/growth': '/admin/codes/issue',
  '/admin/codes/house': '/admin/codes/issue',
  '/admin/codes/batches': '/admin/codes/all',
  '/admin/store-access': '/admin/codes/store',
};
export function legacyNavPath(path: string): string {
  if (LEGACY_FAVORITES[path]) return LEGACY_FAVORITES[path];
  if (/^\/admin\/codes\/batches\/[^/]+$/.test(path)) return '/admin/codes/all';
  return path;
}

// ═════════════════════════════════════════════════════════════════════════════
// roles
// ═════════════════════════════════════════════════════════════════════════════

/** Codes writes (issue, revoke, export): admin and super_admin — the server's CODE_WRITE_ROLES. */
export function canWriteCodes(role: string | null | undefined): boolean {
  return canWrite(role);
}

/** Store setup, the Summer50 reward CSV import, the store inventory run: super_admin only. */
export function isSuperAdmin(role: string | null | undefined): boolean {
  return role === 'super_admin';
}

// ═════════════════════════════════════════════════════════════════════════════
// vocabulary (design §2.2) — every screen says which words it uses
// ═════════════════════════════════════════════════════════════════════════════

export type UnifiedStatus = 'available' | 'issued' | 'redeemed' | 'used_up' | 'expired' | 'revoked' | 'replaced';
/** services/code_status.py UNIFIED_STATUSES without `retired` (an App Store code's word, never in the list). */
export const UNIFIED_STATUSES: UnifiedStatus[] = ['available', 'issued', 'redeemed', 'used_up', 'expired', 'revoked', 'replaced'];
export const UNIFIED_STATUS_LABELS: Record<string, string> = {
  available: 'Available', issued: 'Issued', redeemed: 'Redeemed', used_up: 'Used up', expired: 'Expired',
  revoked: 'Revoked', replaced: 'Replaced', retired: 'Retired',
};

/** The five named families (services/code_catalog.py CATALOG_FAMILIES); a new pooled campaign reads as its name. */
export const NAMED_FAMILIES = ['summer50', 'strategic', 'referral', 'partner', 'house'] as const;
export const FAMILY_LABELS: Record<string, string> = {
  summer50: 'Summer50',
  strategic: 'One-off',
  referral: 'Referral',
  partner: 'Partner',
  house: 'Coach Cedar',
  apple_partner: 'App Store codes (partners)',
  apple_reward: 'App Store codes (Summer50 rewards)',
};

export function familyLabel(family: string | null | undefined): string {
  if (!family) return '—';
  return FAMILY_LABELS[family] ?? `Campaign: ${family}`;
}

export const KIND_LABELS: Record<string, string> = {
  pooled: 'Pool code', one_off: 'One-off', per_user: 'Per person', shared: 'Shared', single_use: 'Single-use',
};

export const CREATED_VIA_OPTIONS = ['admin', 'api', 'cron', 'system', 'manychat', 'store_import'] as const;

/** The line on every screen that shows the unified words (All codes, Overview, the partner's embedded list). */
export const UNIFIED_VOCAB_NOTE =
  'Status uses one set of words for every kind of code: available, issued, redeemed, used up, expired, revoked, replaced. ' +
  "Each code's own word (a partner code's \"active\", a Summer50 code's \"pooled\") is shown beside it.";

/** The line on the screens that keep a family's own words (Code batches, the partner Codes tab). */
export const FAMILY_VOCAB_NOTE =
  "Status here uses each family's own words: active, used up, redeemed, expired, revoked, replaced for partner and " +
  'Coach Cedar codes; pooled, allocated, issued, redeemed for Summer50 and one-off codes. Codes → All codes uses one ' +
  'set of words for every kind (an active code reads "issued" there).';

/** The family filter's choices: the five named families, then any other family the summary counted (a new pooled campaign). */
export function familyOptions(summary: CatalogSummary | null | undefined): string[] {
  const extra = Object.keys(summary?.families ?? {})
    .filter((f) => !(NAMED_FAMILIES as readonly string[]).includes(f))
    .sort();
  return [...NAMED_FAMILIES, ...extra];
}

export type Tone = 'neutral' | 'success' | 'warning' | 'error' | 'info';

export function statusTone(status: string | null | undefined): Tone {
  switch (status) {
    case 'available': return 'info';
    case 'issued': return 'success';
    case 'redeemed': case 'used_up': return 'neutral';
    case 'expired': case 'replaced': return 'warning';
    case 'revoked': return 'error';
    default: return 'neutral';
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// the catalog (GET /api/admin/codes/catalog)
// ═════════════════════════════════════════════════════════════════════════════

export const CATALOG_PAGE_SIZE = 50;

export interface CatalogRow {
  code_id: string;
  source_table: 'campaign_codes' | 'partner_codes';
  code: string;
  family: string;
  kind: string;
  owner_kind: string;
  owner: string;
  partner_id: string | null;
  partner_code: string | null;
  tag_id: string | null;
  tag: string | null;
  family_status: string;
  status: string;
  max_redemptions: number | null;
  uses: number;
  redemption_rows: number;
  deleted_redeemers: number;
  created_via: string;
  batch_id: string | null;
  api_key_id: string | null;
  batch_created_by: string | null;
  wave: string | null;
  grant_duration: string | null;
  /** Admin roles only — dropped for support and viewer. */
  note?: string | null;
  handle?: string | null;
  external_ref?: string | null;
  created_at: string;
  expires_at: string | null;
  allocated_at: string | null;
  last_redeemed_at: string | null;
  revoked_at: string | null;
}

export interface CatalogPage {
  codes: CatalogRow[];
  total: number;
  limit: number;
  offset: number;
  codes_masked: boolean;
}

export interface FamilySummary {
  codes: number;
  uses: number;
  deleted_redeemers: number;
  statuses: Record<string, number>;
}

export interface CatalogSummary {
  families: Record<string, FamilySummary>;
  total: number;
  as_of: string;
}

/** The filters the All codes screen offers (every one a GET /catalog query parameter). */
export interface CatalogFilters {
  code?: string;
  match?: 'exact' | 'prefix' | '';
  family?: string;
  status?: string;
  kind?: string;
  created_via?: string;
  partner_id?: string;
  tag_id?: string;
  batch_id?: string;
  api_key_id?: string;
  created_from?: string;
  created_to?: string;
}

export const CATALOG_FILTER_KEYS: Array<keyof CatalogFilters> = [
  'code', 'match', 'family', 'status', 'kind', 'created_via', 'partner_id', 'tag_id', 'batch_id', 'api_key_id',
  'created_from', 'created_to',
];

/** The filters from the page's address (so `/admin/codes/all?api_key_id=…` opens a key's codes). */
export function filtersFromSearch(search: URLSearchParams): CatalogFilters {
  const f: CatalogFilters = {};
  for (const k of CATALOG_FILTER_KEYS) {
    const v = search.get(k);
    if (v) (f as Record<string, string>)[k] = v;
  }
  return f;
}

/**
 * The catalog's query string: only the filters that are set, then the page. A reader who sees codes
 * masked (support, viewer) never sends a prefix search — the server refuses it (403): it would spell
 * a code out one character at a time.
 */
export function catalogQuery(f: CatalogFilters, page: { limit?: number; offset?: number } = {}, canPrefix = true): string {
  const qs = new URLSearchParams();
  for (const k of CATALOG_FILTER_KEYS) {
    const v = (f[k] ?? '').toString().trim();
    if (!v) continue;
    if (k === 'match' && (v !== 'prefix' || !canPrefix || !(f.code ?? '').trim())) continue;
    qs.set(k, v);
  }
  if (page.limit !== undefined) qs.set('limit', String(page.limit));
  if (page.offset !== undefined) qs.set('offset', String(page.offset));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/** "Showing 51–100 of 1,234". */
export function pageLine(total: number, offset: number, shown: number): string {
  if (!total) return 'No codes match.';
  return `Showing ${(offset + 1).toLocaleString()}–${(offset + shown).toLocaleString()} of ${total.toLocaleString()}`;
}

/** Who holds a code, in the admin's words. */
export function ownerText(r: Pick<CatalogRow, 'owner_kind' | 'owner' | 'partner_code' | 'tag' | 'family'>): string {
  switch (r.owner_kind) {
    case 'house': return r.tag ? `Coach Cedar · ${r.tag}` : 'Coach Cedar';
    case 'partner': return [r.partner_code, r.tag].filter(Boolean).join(' · ') || 'Partner';
    case 'campaign': return r.owner.replace(/^campaign:/, 'Campaign ');
    case 'person': return 'A named person';
    case 'user': return r.owner.replace(/^user:/, 'Member ');
    default: return r.owner || '—';
  }
}

/** "1 of 1", "3 of 5", "7 (no limit)" — and the uses of redeemers who later deleted their accounts. */
export function usesText(r: Pick<CatalogRow, 'uses' | 'max_redemptions' | 'deleted_redeemers'>): string {
  const base = r.max_redemptions == null ? `${r.uses} (no limit)` : `${r.uses} of ${r.max_redemptions}`;
  return r.deleted_redeemers > 0 ? `${base} · ${r.deleted_redeemers} by deleted accounts` : base;
}

/** May this row be revoked from the Codes area (an admin role, a code still usable, a family whose page revokes it)? */
export function revocable(r: Pick<CatalogRow, 'status' | 'family' | 'source_table'>): boolean {
  if (r.status !== 'available' && r.status !== 'issued') return false;
  // A referral code belongs to one member (their five uses): it is never revoked from here.
  return r.source_table === 'partner_codes' || r.family !== 'referral';
}

/** Where a code's own page is — the family's screen, with its writes (extend, limit, allocate). */
export function familyPagePath(r: Pick<CatalogRow, 'source_table' | 'family' | 'partner_id'>): string | null {
  if (r.source_table === 'campaign_codes') return r.family === 'referral' ? null : CODES_ROUTES.issue;
  if (r.family === 'house') return `${CODES_ROUTES.issueHouse}?tab=codes`;
  return r.partner_id ? `/admin/partners/${encodeURIComponent(r.partner_id)}?tab=codes` : null;
}

// ── one code (GET /catalog/codes/{id}) ───────────────────────────────────────

export interface CodeDetail {
  code: CatalogRow;
  codes_masked: boolean;
  ambiguous?: boolean;
  redemptions: Array<Record<string, unknown> & { id: string; redeemed_at: string; user?: string | null }>;
  grant: Record<string, unknown> | null;
  attempts: Array<{ reason: string; user: string | null; at: string }>;
  audit: Array<{ action: string; admin: string | null; success: boolean; error: string | null; at: string; family?: string | null }>;
}

/** A redemption's one line: when, who (`usr_…`, never an email), and what it granted. */
export function redemptionLine(r: CodeDetail['redemptions'][number]): string {
  const parts = [String(r.user || 'a deleted account')];
  if (r.grant_kind) parts.push(String(r.grant_kind).replace(/_/g, ' '));
  if (r.code_kind) parts.push(String(r.code_kind).replace(/_/g, ' '));
  if (r.trial_days) parts.push(`${r.trial_days}-day trial`);
  if (r.discount_monthly_pct) parts.push(`${r.discount_monthly_pct}% off monthly for ${r.discount_monthly_months ?? 12} months`);
  if (r.discount_annual_pct) parts.push(`${r.discount_annual_pct}% off the first year`);
  if (r.attributed === false) parts.push('not attributed');
  return parts.join(' · ');
}

// ═════════════════════════════════════════════════════════════════════════════
// the activity feed (GET /activity)
// ═════════════════════════════════════════════════════════════════════════════

export const ACTIVITY_KINDS = [
  'campaign_redemption', 'campaign_allocation', 'partner_redemption', 'reward_claim', 'apple_allocation',
  'failed_attempt', 'batch_created', 'admin_action',
] as const;
export type ActivityKind = typeof ACTIVITY_KINDS[number];

export const ACTIVITY_KIND_LABELS: Record<ActivityKind, string> = {
  campaign_redemption: 'Redeemed (Summer50, one-off, referral)',
  campaign_allocation: 'Handed out (Summer50)',
  partner_redemption: 'Redeemed (partner, Coach Cedar)',
  reward_claim: 'Referral reward claimed',
  apple_allocation: 'App Store code handed out',
  failed_attempt: 'Failed attempt',
  batch_created: 'Batch made',
  admin_action: 'Admin action',
};

export interface ActivityEvent {
  kind: ActivityKind | string;
  at: string;
  family: string | null;
  code_id: string | null;
  code: string | null;
  user: string | null;
  detail: Record<string, unknown>;
}

export interface ActivityPage {
  events: ActivityEvent[];
  next_cursor: string | null;
  limit: number;
  kinds: string[];
  codes_masked: boolean;
}

export function activityQuery(kinds: string[], cursor: string | null, limit = 50): string {
  const qs = new URLSearchParams();
  if (kinds.length && kinds.length < ACTIVITY_KINDS.length) qs.set('kinds', kinds.join(','));
  if (cursor) qs.set('cursor', cursor);
  qs.set('limit', String(limit));
  return `?${qs.toString()}`;
}

/** One event in the admin's words — never an email, never an App Store code. */
export function describeEvent(e: ActivityEvent): string {
  const d = e.detail || {};
  const who = e.user ? ` by ${e.user}` : '';
  switch (e.kind) {
    case 'campaign_redemption':
      return `${e.code ?? 'A code'} redeemed${who}${d.grant_kind ? ` (${String(d.grant_kind).replace(/_/g, ' ')})` : ''}`;
    case 'campaign_allocation':
      return `${e.code ?? 'A code'} handed out${d.source ? ` via ${d.source}` : ''}`;
    case 'partner_redemption': {
      const whose = e.family === 'house' ? 'Coach Cedar' : String(d.partner_code ?? 'a partner');
      return `${e.code ?? 'A code'} redeemed${who} — ${whose}${d.tag ? ` · ${d.tag}` : ''}${d.attributed === false ? ' (not attributed)' : ''}`;
    }
    case 'reward_claim':
      return `Referral reward claimed${who} (step ${d.rung ?? '?'}, ${d.reward_sku ?? 'reward'})`;
    case 'apple_allocation':
      return `App Store code handed out${who}: ${d.level_pct ?? '?'}% off for ${d.months ?? 12} months${d.tag ? ` · ${d.tag}` : ''}`;
    case 'failed_attempt':
      return `${e.code ?? 'A code'} refused${who}: ${String(d.reason ?? 'unknown reason').replace(/_/g, ' ')}`;
    case 'batch_created':
      return `Batch made (${String(d.kind ?? 'codes').replace(/_/g, ' ')}, via ${d.created_via ?? '?'})${d.requested ? `, ${d.requested} asked for` : ''}`;
    case 'admin_action':
      return `${String(d.action ?? 'An admin action').replace(/^codes\./, '').replace(/_/g, ' ')}${d.admin ? ` by admin ${d.admin}` : ''}${d.success === false ? ' — failed' : ''}`;
    default:
      return String(e.kind);
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// API keys (GET /api-summary)
// ═════════════════════════════════════════════════════════════════════════════

export interface ApiKeySummaryRow {
  id: string;
  partner_id: string;
  partner_code: string;
  partner_name: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  rate_limit_per_minute: number;
  daily_mint_quota: number;
  state: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  rotated_to_key_id: string | null;
  calls_24h: number;
  codes_created_24h: number;
  quota_remaining_24h: number;
  quota_used_fraction: number;
}

export interface ApiSummary {
  quota_source: string;
  partner_api_live: boolean;
  totals: { keys: number; active: number; codes_created_24h: number; calls_24h: number };
  keys: ApiKeySummaryRow[];
}

export const QUOTA_WARN_FRACTION = 0.8;

export function quotaLine(k: Pick<ApiKeySummaryRow, 'codes_created_24h' | 'daily_mint_quota' | 'quota_used_fraction'>): string {
  const pct = Math.round((k.quota_used_fraction || 0) * 100);
  return `${k.codes_created_24h.toLocaleString()} of ${k.daily_mint_quota.toLocaleString()} codes in 24 h (${pct}%)`;
}

export const HOUSE_NO_API_NOTE = 'Coach Cedar codes are never made through the partner API — only here, by an admin.';

// ═════════════════════════════════════════════════════════════════════════════
// store inventory (GET /inventory)
// ═════════════════════════════════════════════════════════════════════════════

export interface StoreBatch {
  family: 'apple_partner' | 'apple_reward';
  batch_key: string;
  batch_id: string | null;
  batch_ref: string | null;
  owner: string;
  store?: string | null;
  product_id?: string | null;
  level_pct?: number | null;
  months?: number | null;
  reward_sku?: string | null;
  batch_status: string;
  requested_count: number | null;
  store_reported_count: number | null;
  in_inventory: number;
  available: number;
  issued: number;
  redeemed: number | null;
  expired: number;
  retired: number;
  expires_at?: string | null;
  next_free_expiry?: string | null;
  source: string;
  created_at?: string | null;
}

export interface QuarterCount { counts: string; used: number; remaining: number; label: string }

export interface InventorySummary {
  batches: StoreBatch[];
  totals: Record<string, Record<string, number>>;
  quarter: { start: string; limit: number; partner_batches: number; summer50_imported: number; cron: QuarterCount; estimate: QuarterCount };
}

/** What an App Store batch's codes are for. */
export function storeBatchOffer(b: StoreBatch): string {
  if (b.family === 'apple_reward') return `Summer50 reward · ${b.reward_sku ?? '?'}`;
  const product = (b.product_id ?? '').replace(/^com\.coachcedar\.app\./, '');
  return `${b.level_pct ?? '?'}% off for ${b.months ?? 12} months · ${product || '?'}`;
}

/** "500 asked · 500 Apple said · 500 in inventory" — the three differ only when something drifted. */
export function storeBatchCounts(b: StoreBatch): string {
  const parts: string[] = [];
  if (b.requested_count != null) parts.push(`${b.requested_count} asked`);
  if (b.store_reported_count != null) parts.push(`${b.store_reported_count} Apple said`);
  parts.push(`${b.in_inventory} in inventory`);
  return parts.join(' · ');
}

export function storeBatchDrifts(b: StoreBatch): boolean {
  return b.store_reported_count != null && b.store_reported_count !== b.in_inventory;
}

export const LOW_STOCK_AVAILABLE = 20;
export const EXPIRY_WARN_DAYS = 30;

function daysUntil(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.floor((t - now.getTime()) / 86_400_000);
}

// ═════════════════════════════════════════════════════════════════════════════
// offers (GET /offers)
// ═════════════════════════════════════════════════════════════════════════════

export interface OfferCombo {
  pct: number;
  months: number;
  status: string;
  live_tags_monthly: number;
  live_tags_annual: number;
  grants_monthly: number;
  grants_annual: number;
}

export interface OffersView {
  combos: OfferCombo[];
  slots?: ProductSlots[];
  active_cap?: number;
  external_offers?: unknown[];
  [k: string]: unknown;
}

export function comboLabel(c: Pick<OfferCombo, 'pct' | 'months'>): string {
  return c.months === 12 ? `${c.pct}% off · 12 months (or the first year)` : `${c.pct}% off · ${c.months} month${c.months === 1 ? '' : 's'}`;
}

export function slotText(s: Pick<ProductSlots, 'used' | 'cap'>, cap?: number): string {
  const used = Number(s.used ?? 0);
  const of = Number(s.cap ?? cap ?? 10);
  return `${used} of ${of} offer slots used`;
}

// ═════════════════════════════════════════════════════════════════════════════
// Overview
// ═════════════════════════════════════════════════════════════════════════════

export interface FamilyTile { family: string; label: string; codes: number; issued: number; redeemed: number; expired: number; available: number }

/** One tile per family with codes (the named five always, a new campaign when it has codes). */
export function familyTiles(summary: CatalogSummary | null | undefined): FamilyTile[] {
  if (!summary) return [];
  return familyOptions(summary)
    .filter((f) => (NAMED_FAMILIES as readonly string[]).includes(f) || (summary.families[f]?.codes ?? 0) > 0)
    .map((f) => {
      const s = summary.families[f]?.statuses ?? {};
      return {
        family: f,
        label: familyLabel(f),
        codes: summary.families[f]?.codes ?? 0,
        issued: s.issued ?? 0,
        redeemed: (s.redeemed ?? 0) + (s.used_up ?? 0),
        expired: s.expired ?? 0,
        available: s.available ?? 0,
      };
    });
}

export interface Attention { tone: 'warn' | 'info'; text: string; link?: string }

/**
 * What needs an admin's eye, read from the other screens' answers: App Store code stock running low or
 * expiring, a key near its daily quota, a product's offer slots nearly full. Nothing is stored.
 */
export function attentionItems(
  inv: InventorySummary | null | undefined,
  api: ApiSummary | null | undefined,
  offers: OffersView | null | undefined,
  now: Date = new Date(),
): Attention[] {
  const out: Attention[] = [];
  for (const b of inv?.batches ?? []) {
    const live = b.batch_status !== 'retired' && b.in_inventory > 0;
    if (!live) continue;
    if (b.available < LOW_STOCK_AVAILABLE) {
      out.push({ tone: 'warn', text: `${storeBatchOffer(b)}: only ${b.available} unused codes left.`, link: CODES_ROUTES.store });
    }
    const days = daysUntil(b.next_free_expiry ?? b.expires_at, now);
    if (days !== null && days <= EXPIRY_WARN_DAYS && b.available > 0) {
      out.push({
        tone: days <= 7 ? 'warn' : 'info',
        text: `${storeBatchOffer(b)}: ${b.available} unused codes expire in ${Math.max(days, 0)} day${days === 1 ? '' : 's'}.`,
        link: CODES_ROUTES.store,
      });
    }
    if (storeBatchDrifts(b)) {
      out.push({ tone: 'info', text: `${storeBatchOffer(b)}: Apple said ${b.store_reported_count}, inventory holds ${b.in_inventory}.`, link: CODES_ROUTES.store });
    }
  }
  for (const k of api?.keys ?? []) {
    if (k.state === 'active' && (k.quota_used_fraction ?? 0) >= QUOTA_WARN_FRACTION) {
      out.push({ tone: 'warn', text: `API key ${k.key_prefix} (${k.partner_name}): ${quotaLine(k)}.`, link: CODES_ROUTES.api });
    }
  }
  for (const s of offers?.slots ?? []) {
    const cap = Number(s.cap ?? offers?.active_cap ?? 10);
    const used = Number(s.used ?? 0);
    if (cap > 0 && used >= cap - 1) {
      out.push({ tone: used >= cap ? 'warn' : 'info', text: `${s.product_id ?? 'A product'}: ${slotText(s, cap)}.`, link: CODES_ROUTES.offers });
    }
  }
  return out;
}

// ═════════════════════════════════════════════════════════════════════════════
// the Summer50 reward CSV import (POST /api/admin/growth/inventory/import, super_admin)
// ═════════════════════════════════════════════════════════════════════════════

/** services/referral_service.py SKU_MONTH / SKU_ANNUAL — the reward codes the import stocks. */
export const REWARD_SKUS = [
  { sku: 'chatplus_50_month', label: '50% off one Chat+ month (steps 1 and 2)' },
  { sku: 'chatplus_annual_194', label: 'Chat+ annual at $194 (step 3)' },
] as const;

export interface RewardImportForm { sku: string; batchRef: string; expires: string; fileName: string | null }

/** Why the import can't be sent yet, in the admin's words (null when it can). */
export function rewardImportError(f: RewardImportForm, today: string): string | null {
  if (!REWARD_SKUS.some((r) => r.sku === f.sku)) return 'Choose which reward the codes are for.';
  if (!f.batchRef.trim()) return "Give the batch a name (App Store Connect's batch reference is best).";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.expires)) return 'Enter the date the codes expire (from App Store Connect).';
  if (f.expires <= today) return 'That date has passed: codes that have expired cannot be handed out.';
  if (!f.fileName) return "Choose the CSV App Store Connect gave you (one code per line, or a 'code' column).";
  return null;
}

export function rewardImportQuery(f: RewardImportForm): string {
  const qs = new URLSearchParams({ reward_sku: f.sku, batch_ref: f.batchRef.trim(), batch_expires_at: `${f.expires}T23:59:59Z` });
  return `?${qs.toString()}`;
}

// ═════════════════════════════════════════════════════════════════════════════
// Issue codes — the owner's rules, stated where codes are made
// ═════════════════════════════════════════════════════════════════════════════

export const ISSUE_RULES: string[] = [
  "A person's first source counts: a partner link they open later earns that partner nothing.",
  'A second discount code replaces the one they had.',
  'Coach Cedar codes are made here only, never through the partner API.',
  'Summer50 reward codes keep their own supply (Store inventory).',
];
